import { IScene } from '@webgl/engine/IScene'
import { vec4 } from 'gl-matrix'
import { GLContext } from 'nanogl/types'
import Camera from 'nanogl-camera'
import GLState from 'nanogl-state/GLState'

import { HexToNewVec3 } from '@/webgl/core/Color'
import RenderPass from '@/webgl/core/RenderPass'
import FPSLimiter from '@/webgl/FPSLimiter'
import AssetDatabase from '@/webgl/resources/AssetDatabase'
import Postprocess from '@/webgl/runner/rendering/Postprocess'

import Cameras from './cameras/Cameras'
import Capabilities from './core/Capabilities'
import Pointers from './core/Pointers'
import { MainRenderContext } from './core/RenderContext'
import RenderMask from './core/RenderMask'
import Viewport from './core/Viewport'
import DebugDraw from './dev/debugDraw/DebugDraw'
import GLView from './GLView'

export default class Renderer {
  /**
   * the HTMLElemment used to listen user inputs
   */
  ilayer: HTMLElement

  /**
   * cameras manager
   */
  cameras: Cameras

  pointers: Pointers

  /**
   * Clear color: cream
   */
  clearColor = vec4.fromValues(1, 0.957, 0.898, 1)

  /**
   * main backbuffer viewport
   */
  readonly viewport = new Viewport()

  readonly context: MainRenderContext

  postprocess: Postprocess

  scene: IScene

  public dt: number = 0
  public limiter: FPSLimiter

  constructor(readonly glview: GLView) {
    glview.onRender.on(this._onViewRender)

    this.ilayer = glview.canvas

    DebugDraw.init(glview.gl)

    this.postprocess = new Postprocess(this)

    this.context = new MainRenderContext(this.gl, this.viewport)
    this.pointers = new Pointers(this.ilayer)
    this.cameras = new Cameras(this)
    this.limiter = new FPSLimiter()
    this.limiter.fps = 31
    this.limiter.enabled = true
    this.limiter.accum = 1 / 31

    this.clear()

    AssetDatabase.printAssets()
    Capabilities(this.gl)
    //.report()
  }

  public setScene(scene: IScene) {
    this.scene = scene
  }

  get gl(): GLContext {
    return this.glview.gl
  }

  get camera(): Camera {
    return this.cameras.camera
  }

  get width(): number {
    return this.glview.width
  }

  get height(): number {
    return this.glview.height
  }

  setClearColor(color: number) {
    this.clearColor.set(HexToNewVec3(color))
  }

  private _onViewRender = (dt: number) => {
    this.context.withCamera(this.camera)
    this.viewport.setSize(this.glview.width, this.glview.height)

    if (this.limiter.step(dt)) {
      this.dt = this.limiter.dt
      this.renderScene(this.scene)
    }

    this.pointers.endFrame()

    /// #if _DEBUG
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((GLState.get(this.gl).cfgStack as any)._ptr > 0) {
      throw new Error('glstate overflow')
    }
    /// #endif
  }

  private renderScene(scene: IScene) {
    const gl = this.gl

    this.cameras.preRender()
    scene?.preRender()
    this.camera.updateViewProjectionMatrix(this.viewport.width, this.viewport.height)
    this.postprocess.viewport(this.viewport.width, this.viewport.height)
    this.postprocess.preRender(this.dt)

    scene?.rttPass(this.context.withMask(RenderMask.OPAQUE).withPass(RenderPass.COLOR_MASK))
    this.context.withPass(RenderPass.DEFAULT)

    const c = this.clearColor
    gl.clearColor(c[0], c[1], c[2], c[3])
    this.postprocess.post.clearColor.set(c)
    this.postprocess.bindColor()

    DebugDraw.render(this.context)

    scene?.render(this.context.withMask(RenderMask.OPAQUE))
    scene?.render(this.context.withMask(RenderMask.BLENDED))
    GLState.get(this.gl).apply()

    this.postprocess.render()
  }

  clear() {
    const gl = this.gl
    const c = this.clearColor
    gl.clearColor(c[0], c[1], c[2], c[3])
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    this.viewport.setupGl(gl)
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
  }

  dispose() {
    this.pointers.dispose()
  }
}
